<template>
  <div v-if="loading" class="text-center q-pa-md">
    <q-spinner color="blue-brand" size="2em" />
    <div class="text-caption text-grey-7 q-mt-xs">Loading summary…</div>
  </div>
  <div v-else-if="runData" class="pig-run-summary q-mt-sm">
    <div class="summary-grid">
      <div class="summary-row">
        <span class="summary-label">Pig Run</span>
        <span class="summary-value">{{ runData.name }}</span>
      </div>
      <div class="summary-row">
        <span class="summary-label">Pipeline</span>
        <span class="summary-value">{{
          runData.pipelineSectionName || 'N/A'
        }}</span>
      </div>
      <div class="summary-row">
        <span class="summary-label">Launch</span>
        <span class="summary-value">{{ formatDate(runData.launchDate) }}</span>
      </div>
      <div class="summary-row">
        <span class="summary-label">Receive</span>
        <span class="summary-value">{{ formatDate(runData.receiveDate) }}</span>
      </div>
      <div class="summary-row">
        <span class="summary-label">Markers</span>
        <span class="summary-value">{{ markerCount }}</span>
      </div>
      <div class="summary-row">
        <span class="summary-label">Devices</span>
        <span class="summary-value">{{ deviceCount }}</span>
      </div>
      <div class="summary-row">
        <span class="summary-label">Passages</span>
        <span class="summary-value">{{ runData.passageCount ?? 0 }}</span>
      </div>
      <div class="summary-row">
        <span class="summary-label">Verified</span>
        <span class="summary-value">{{
          runData.verifiedPassageCount ?? 0
        }}</span>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, markRaw } from 'vue';
import L from 'leaflet';
import * as dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import PigRunService from '@/services/PigRunService';
import PigRunCommon from '@/utils/PigRunCommon';
import { escapeHtml } from '@/utils/HtmlEscape';

dayjs.extend(utc);

export default defineComponent({
  name: 'PigRunSummary',
  props: {
    pigRunId: {
      type: Number,
      default: undefined,
    },
    skipPlot: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['loaded'],
  data() {
    return {
      loading: false,
      runData: null as any,
      mapPreview: null as L.Polyline | null,
      mapMarkers: [] as L.Marker[],
      previewCircles: [] as L.Circle[],
    };
  },
  computed: {
    markerCount(): number {
      return this.runData?.markers?.length ?? 0;
    },
    deviceCount(): number {
      return this.runData?.devices?.length ?? 0;
    },
    mainMap(): L.Map | null {
      return this.$store.state.map;
    },
  },
  watch: {
    pigRunId(newId: number | undefined) {
      if (newId) {
        this.fetchSummary(newId);
      } else {
        this.cleanup();
      }
    },
  },
  mounted() {
    if (this.pigRunId) {
      this.fetchSummary(this.pigRunId);
    }
  },
  beforeUnmount() {
    this.cleanup();
  },
  methods: {
    formatDate(date: string | null | undefined): string {
      if (!date) return 'N/A';
      const raw = String(date).trim().replace(' ', 'T');
      const utcStr = raw.endsWith('Z') ? raw : raw + 'Z';
      return dayjs.default(utcStr).utc().format('YYYY-MM-DD HH:mm:ss');
    },
    async fetchSummary(pigRunId: number) {
      this.loading = true;
      this.runData = null;
      this.removeMapPreview();
      try {
        // Use cached data from store if available for this pig run
        const cached = this.$store.state.plottedPigRunData;
        if (cached && cached.pigRunId === pigRunId) {
          this.runData = cached;
        } else {
          const response = await PigRunService.getPigRun(pigRunId);
          this.runData = response.data;
          this.$store.commit('SET_PLOTTED_PIG_RUN_DATA', this.runData);
        }
        this.loading = false;
        this.$emit('loaded', this.runData);
        if (!this.skipPlot && this.$store.state.plottedPigRunId !== pigRunId) {
          this.plotOnMainMap();
        }
      } catch (error) {
        console.error('Failed to fetch pig run summary:', error);
        this.runData = null;
        this.loading = false;
      }
    },
    async refresh() {
      if (!this.pigRunId) return;
      // Force re-fetch by clearing the cache first
      this.$store.commit('SET_PLOTTED_PIG_RUN_DATA', null);
      await this.fetchSummary(this.pigRunId);
    },
    plotOnMainMap() {
      if (!this.mainMap || !this.runData) return;

      const markers = this.runData.markers;
      if (!markers || markers.length === 0) return;

      const sorted = [...markers].sort(
        (a: any, b: any) => a.sortOrder - b.sortOrder
      );
      const latLngs: L.LatLng[] = sorted
        .filter((m: any) => m.latitude && m.longitude)
        .map((m: any) => L.latLng(m.latitude, m.longitude));

      if (latLngs.length === 0) return;

      if (latLngs.length >= 2) {
        this.mapPreview = markRaw(
          L.polyline(latLngs, {
            color: '#1976D2',
            weight: 3,
            opacity: 0.8,
          }).addTo(this.mainMap)
        );
      }

      // Add marker icons and 100m circles
      for (const marker of sorted) {
        if (!marker.latitude || !marker.longitude) continue;

        const markerLatLng = L.latLng(marker.latitude, marker.longitude);

        const circle = L.circle(markerLatLng, {
          radius: 100,
          color: '#2196F3',
          fillColor: '#2196F3',
          fillOpacity: 0.1,
          weight: 1,
        }).addTo(this.mainMap);
        this.previewCircles.push(markRaw(circle));

        let markerIcon: L.Icon | L.DivIcon;
        if (marker.description === 'LAUNCH') {
          markerIcon = PigRunCommon.launchIcon;
        } else if (marker.description === 'RECEIVE') {
          markerIcon = PigRunCommon.recieveIcon;
        } else if (marker.trackState === 'S') {
          markerIcon = PigRunCommon.skipIcon;
        } else {
          markerIcon = marker.hasVerifiedPassage
            ? PigRunCommon.trackIcon
            : PigRunCommon.trackIcon_2;
        }

        const leafletMarker = L.marker(markerLatLng, {
          icon: markerIcon,
        }).addTo(this.mainMap);

        leafletMarker.bindTooltip(escapeHtml(marker.name), {
          permanent: false,
          direction: 'top',
          offset: [0, -8],
        });

        leafletMarker.on('click', () => {
          this.$store.dispatch('setSelectedMarker', {
            pigRunId: this.runData.pigRunId,
            pipelineSectionMarkerId: marker.pipelineSectionMarkerId,
            name: marker.name,
            lat: marker.latitude,
            lon: marker.longitude,
            trackState: marker.trackState,
            description: marker.description,
          });
        });

        this.mapMarkers.push(markRaw(leafletMarker));
      }

      this.$store.commit('SET_PLOTTED_PIG_RUN_ID', this.pigRunId);

      this.mainMap.fitBounds(L.latLngBounds(latLngs), {
        maxZoom: 15,
        padding: [50, 50],
      });
    },
    removeMapPreview() {
      if (this.mapPreview && this.mainMap) {
        this.mapPreview.removeFrom(this.mainMap);
        this.mapPreview = null;
      }
      for (const m of this.mapMarkers) {
        if (this.mainMap) m.removeFrom(this.mainMap);
      }
      this.mapMarkers = [];
      for (const c of this.previewCircles) {
        if (this.mainMap) c.removeFrom(this.mainMap);
      }
      this.previewCircles = [];
    },
    cleanup() {
      this.removeMapPreview();
      this.runData = null;
    },
  },
});
</script>

<style scoped>
.pig-run-summary {
  background: #f5f5f5;
  border-radius: 4px;
  padding: 8px;
  font-size: 12px;
}
.summary-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2px 12px;
}
.summary-row {
  display: flex;
  justify-content: space-between;
  padding: 1px 0;
}
.summary-label {
  color: #757575;
  font-weight: 500;
}
.summary-value {
  color: #212121;
  text-align: right;
  word-break: break-word;
}
</style>
